import { MoreOutlined, StarFilled } from '@ant-design/icons';
import {
  Button,
  Dropdown,
  MenuProps,
  Progress,
  Table,
  TablePaginationConfig,
  Tag,
  Tooltip,
} from 'antd';
import { useTranslation } from 'react-i18next';

import {
  OperationRequest,
  OperationRequestStatus,
} from 'models/OperationRequest';

import { pctOf } from 'services/OperationRequirementService';

import { computeStartsInFromWeek } from '../data/operationCatalogs';

import './OpsRequestTable.scss';

interface OpsRequestTableProps {
  dataSource: OperationRequest[];
  loading?: boolean;
  page: number;
  pageSize: number;
  total: number;
  pageSizeOptions?: number[];
  onPaginationChange: (page: number, pageSize: number) => void;
  onAssign: (row: OperationRequest) => void;
  onView: (row: OperationRequest) => void;
  onEdit: (row: OperationRequest) => void;
  onCancel: (row: OperationRequest) => void;
  onContinueDraft: (row: OperationRequest) => void;
  onDeleteDraft: (row: OperationRequest) => void;
}

const STATUS_CLASS: Record<OperationRequestStatus, string> = {
  borrador: 'slate',
  pendiente: 'gray',
  'en-curso': 'blue',
  riesgo: 'amber',
  listo: 'green',
  publicado: 'teal',
  cancelado: 'red',
};

function progressStatus(pct: number): 'success' | 'normal' | 'exception' {
  if (pct >= 100) return 'success';
  if (pct <= 25) return 'exception';
  return 'normal';
}

function progressStroke(pct: number): string | undefined {
  if (pct >= 100) return undefined;
  if (pct <= 25) return '#fa8c16';
  if (pct < 70) return '#00c0b3';
  return undefined;
}

function isUnassignedSupervisor(value: string | undefined | null) {
  const name = (value || '').trim();
  if (!name) return true;
  return /^sin asignar$/i.test(name) || name === '—' || name === '-';
}

function OpsRequestTable({
  dataSource,
  loading,
  page,
  pageSize,
  total,
  pageSizeOptions = [10, 20, 50],
  onPaginationChange,
  onAssign,
  onView,
  onEdit,
  onCancel,
  onContinueDraft,
  onDeleteDraft,
}: OpsRequestTableProps) {
  const { t } = useTranslation();
  const i18nBase = 'scenes.requirements_operation.scene';

  const getRowMenu = (row: OperationRequest): MenuProps['items'] => {
    if (row.status === 'borrador') {
      return [
        {
          key: 'continue',
          label: t(`${i18nBase}.dropdown.continue_draft`),
          onClick: () => onContinueDraft(row),
        },
        { type: 'divider' },
        {
          key: 'delete-draft',
          danger: true,
          label: t(`${i18nBase}.dropdown.delete_draft`),
          onClick: () => onDeleteDraft(row),
        },
      ];
    }

    if (row.status === 'cancelado') {
      return [];
    }

    const locked = row.status === 'publicado';
    return [
      {
        key: 'edit',
        label: t(`${i18nBase}.dropdown.edit`),
        disabled: locked,
        onClick: () => onEdit(row),
      },
      {
        key: 'assign',
        label: t(`${i18nBase}.dropdown.assign`),
        disabled: locked,
        onClick: () => onAssign(row),
      },
      { type: 'divider' },
      {
        key: 'cancel',
        danger: true,
        label: t(`${i18nBase}.dropdown.cancel`),
        onClick: () => onCancel(row),
      },
    ];
  };

  const pagination: TablePaginationConfig = {
    current: page,
    pageSize,
    total,
    showSizeChanger: true,
    pageSizeOptions: pageSizeOptions.map(String),
    showQuickJumper: false,
    position: ['bottomRight'],
    onChange: (nextPage, nextPageSize) => {
      onPaginationChange(nextPage, nextPageSize || pageSize);
    },
  };

  const startLabel = (startsIn: number) => {
    if (startsIn < 0) {
      return t(`${i18nBase}.start.in_days`, { days: startsIn });
    }
    if (startsIn === 0) return t(`${i18nBase}.start.today`);
    if (startsIn === 1) return t(`${i18nBase}.start.tomorrow`);
    return t(`${i18nBase}.start.in_days`, { days: startsIn });
  };

  const resolveStartsIn = (row: OperationRequest) =>
    computeStartsInFromWeek(row.week, row.startDate);

  return (
    <div className="ops-request-table">
      <Table<OperationRequest>
        rowKey="id"
        loading={loading}
        dataSource={dataSource}
        pagination={pagination}
        scroll={{ x: 1180 }}
        size="middle"
        rowClassName={(record) =>
          record.status === 'borrador' ? 'ops-request-table__row--draft' : ''
        }
      >
        <Table.Column<OperationRequest>
          title={t(`${i18nBase}.table.columns.request`)}
          key="id"
          width={148}
          render={(_, record) => (
            <div className="ops-request-table__id-cell">
              <button
                type="button"
                className="ops-request-table__id"
                onClick={() =>
                  record.status === 'borrador'
                    ? onContinueDraft(record)
                    : onView(record)
                }
              >
                {record.id}
              </button>
              <div className="ops-request-table__week">{record.week}</div>
            </div>
          )}
        />

        <Table.Column<OperationRequest>
          title={t(`${i18nBase}.table.columns.brand`)}
          key="brand"
          render={(_, record) => (
            <div className="ops-request-table__brand-cell">
              <div className="ops-request-table__brand">{record.brand}</div>
              <div className="ops-request-table__campaign">
                {record.campaign || '—'}
              </div>
              <div className="ops-request-table__tags">
                {record.profile ? (
                  <Tag className="ops-request-table__tag">{record.profile}</Tag>
                ) : null}
                <Tooltip title={t(`${i18nBase}.contract.${record.type}`)}>
                  <Tag className="ops-request-table__tag">
                    {t(`${i18nBase}.contract_short.${record.type}`)}
                  </Tag>
                </Tooltip>
                <Tag className="ops-request-table__tag">
                  {t(`${i18nBase}.table.stores_count`, {
                    count: record.stores,
                  })}
                </Tag>
                {record.vipStores > 0 && (
                  <Tag className="ops-request-table__vip-tag">
                    <StarFilled /> {record.vipStores} VIP
                  </Tag>
                )}
              </div>
            </div>
          )}
        />

        <Table.Column<OperationRequest>
          title={t(`${i18nBase}.table.columns.shifts`)}
          key="turnos"
          width={108}
          align="center"
          render={(_, record) => (
            <div className="ops-request-table__shifts">
              <div className="ops-request-table__shifts-main">
                {record.turnos}
              </div>
              <div className="ops-request-table__shifts-sub">
                {t(`${i18nBase}.table.slots_of`, { slots: record.slots })}
              </div>
            </div>
          )}
        />

        <Table.Column<OperationRequest>
          title={t(`${i18nBase}.table.columns.assignment`)}
          key="filled"
          width={200}
          render={(_, record) => {
            if (record.status === 'borrador') {
              return (
                <div className="ops-request-table__assign ops-request-table__assign--muted">
                  {t(`${i18nBase}.table.draft_no_assignment`)}
                </div>
              );
            }
            if (record.status === 'cancelado') {
              return (
                <div className="ops-request-table__assign ops-request-table__assign--muted">
                  {t(`${i18nBase}.table.cancelled_assignment`)}
                </div>
              );
            }
            const pct =
              record.assignmentPct !== undefined
                ? record.assignmentPct
                : pctOf(record);
            const missing =
              record.missing !== undefined
                ? record.missing
                : Math.max(0, record.slots - record.filled);
            return (
              <div className="ops-request-table__assign">
                <div className="ops-request-table__assign-label">
                  <strong>{pct}%</strong>{' '}
                  <span>
                    {t(`${i18nBase}.table.assigned_fraction`, {
                      filled: record.filled,
                      slots: record.slots,
                    })}
                  </span>
                </div>
                <Progress
                  className="ops-request-table__assign-bar"
                  percent={pct}
                  size="small"
                  showInfo={false}
                  status={progressStatus(pct)}
                  strokeColor={progressStroke(pct)}
                  trailColor="#e8ecf1"
                />
                {missing > 0 && pct < 100 ? (
                  <div className="ops-request-table__assign-missing">
                    {t(`${i18nBase}.table.missing_people`, { count: missing })}
                  </div>
                ) : pct >= 100 ? (
                  <div className="ops-request-table__assign-complete">
                    {t(`${i18nBase}.table.assignment_complete`)}
                  </div>
                ) : null}
              </div>
            );
          }}
        />

        <Table.Column<OperationRequest>
          title={t(`${i18nBase}.table.columns.start`)}
          key="startsIn"
          width={128}
          render={(_, record) => {
            if (record.status === 'borrador') {
              return (
                <div className="ops-request-table__start ops-request-table__start--muted">
                  {t(`${i18nBase}.table.draft_start`)}
                </div>
              );
            }
            const startsIn = resolveStartsIn(record);
            const overdue = startsIn < 0;
            const urgent = overdue || (startsIn <= 2 && pctOf(record) < 100);
            const soon = !urgent && startsIn <= 3;
            return (
              <div
                className={`ops-request-table__start${
                  urgent
                    ? ' ops-request-table__start--urgent'
                    : soon
                    ? ' ops-request-table__start--soon'
                    : ''
                }`}
              >
                {urgent && (
                  <span className="ops-request-table__bang" aria-hidden>
                    !
                  </span>
                )}
                <span>{startLabel(startsIn)}</span>
              </div>
            );
          }}
        />

        <Table.Column<OperationRequest>
          title={t(`${i18nBase}.table.columns.status`)}
          key="status"
          width={168}
          render={(_, record) => (
            <div
              className={`ops-request-table__status ops-request-table__status--${
                STATUS_CLASS[record.status]
              }`}
            >
              <span className="ops-request-table__status-dot" />
              <span>{t(`${i18nBase}.status.${record.status}`)}</span>
            </div>
          )}
        />

        <Table.Column<OperationRequest>
          title={t(`${i18nBase}.table.columns.supervisor`)}
          key="supervisor"
          width={156}
          render={(_, record) => {
            const unassigned =
              record.status === 'borrador' ||
              isUnassignedSupervisor(record.supervisor);
            return (
              <div className="ops-request-table__supervisor">
                <div
                  className={`ops-request-table__supervisor-name${
                    unassigned
                      ? ' ops-request-table__supervisor-name--muted'
                      : ''
                  }`}
                >
                  {unassigned ? '—' : record.supervisor}
                </div>
                <div className="ops-request-table__supervisor-kam">
                  {t(`${i18nBase}.table.kam_prefix`, { name: record.kam })}
                </div>
              </div>
            );
          }}
        />

        <Table.Column<OperationRequest>
          key="actions"
          width={52}
          align="center"
          fixed="right"
          render={(_, record) => {
            const items = getRowMenu(record);
            if (!items || items.length === 0) {
              return null;
            }
            return (
              <Dropdown
                menu={{ items }}
                trigger={['click']}
                placement="bottomRight"
              >
                <Button
                  type="text"
                  className="ops-request-table__actions-btn"
                  icon={<MoreOutlined />}
                  aria-label={t(`${i18nBase}.dropdown.aria`)}
                />
              </Dropdown>
            );
          }}
        />
      </Table>
    </div>
  );
}

export default OpsRequestTable;
